import { cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/** Which column the consumer is sorting by, and in which direction. */
export interface TableSort {
	key: string
	direction: 'ascending' | 'descending'
}

/** Row-padding density. `comfortable` is the default; `compact` packs more rows per screen. */
export enum TableDensity {
	Comfortable = 'comfortable',
	Compact = 'compact',
}

/** Vertical cell padding per density; horizontal padding is constant. */
const DENSITY_CELL_PADDING: Record<TableDensity, string> = {
	[TableDensity.Comfortable]: 'px-3 py-2',
	[TableDensity.Compact]: 'px-3 py-1',
}

/**
 * One column definition.
 *
 * @property key - Property read from a row when no `cell` renderer is supplied.
 * @property header - Column header content (text, or a node such as a select-all checkbox).
 * @property cell - Optional renderer; receives the row and returns its cell content.
 * @property sortable - When true the header becomes a sort button.
 */
export interface TableColumn<Row> {
	key: string
	header: ReactNode
	cell?: (row: Row) => ReactNode
	sortable?: boolean
}

/**
 * Props for the {@link Table} molecule.
 *
 * @property columns - Column definitions, in render order.
 * @property rows - Rows in the order they should render; the table never reorders them.
 * @property sort - The consumer's current sort, reflected as `aria-sort`.
 * @property onSortChange - Reports the next sort when a sortable header is activated.
 * @property emptyState - Rendered in place of the rows when `rows` is empty.
 * @property density - Row-padding density; defaults to `comfortable`.
 * @property className - Extra Tailwind classes merged onto the table.
 *
 * @example
 * ```tsx
 * <Table
 *   aria-label="Report"
 *   columns={[{ key: 'name', header: 'Name', sortable: true }]}
 *   rows={rows}
 *   sort={sort}
 *   onSortChange={setSort}
 *   emptyState={<EmptyState title="No results" />}
 * />
 * ```
 */
export interface TableProps<Row> {
	columns: TableColumn<Row>[]
	rows: Row[]
	sort?: TableSort
	onSortChange?: (sort: TableSort) => void
	emptyState?: ReactNode
	density?: TableDensity
	className?: string
	'aria-label'?: string
}

/** ascending ⇄ descending on the active column; any other column starts ascending. */
const nextSort = (key: string, sort: TableSort | undefined): TableSort => ({
	key,
	direction: sort?.key === key && sort.direction === 'ascending' ? 'descending' : 'ascending',
})

/** `aria-sort` for a column: its direction when active, `none` otherwise. */
const sortStateOf = (key: string, sort: TableSort | undefined): 'ascending' | 'descending' | 'none' =>
	sort?.key === key ? sort.direction : 'none'

/**
 * Generic data table — headers from `columns`, rows from `rows`.
 *
 * @remarks
 * - **Presentational only:** it renders the rows in the order given and **never
 *   sorts, filters or paginates**. Activating a sortable header reports the next
 *   `{ key, direction }` through `onSortChange`; the consumer's hook does the
 *   sorting. This keeps it usable with server-side sorting unchanged.
 * - **Accessibility:** native `<table>` semantics with `<th scope="col">`
 *   headers and `aria-sort` reflecting the current sort. A sortable header is a
 *   button, so it is reachable and operable by keyboard.
 * - **Empty:** when `rows` is empty the body becomes one cell spanning every
 *   column, rendering `emptyState`.
 * - **Styling:** every colour, radius and spacing class is a `ds-*` token utility.
 *
 * @see packages/ui-components/src/table/table.stories.tsx for visual examples
 */
/** One column header — a sort button when the column is sortable, plain text otherwise. */
function HeaderCell<Row>({
	column,
	sort,
	onSortChange,
}: {
	column: TableColumn<Row>
	sort?: TableSort
	onSortChange?: (sort: TableSort) => void
}): ReactElement {
	return (
		<th
			scope="col"
			// Every header advertises its sort state — `none` for the columns not
			// currently sorted — as the WAI-ARIA sortable-table pattern prescribes.
			aria-sort={sortStateOf(column.key, sort)}
			className="px-3 py-2 text-left text-ds-sm font-medium text-ds-text-muted"
		>
			{column.sortable ? (
				<button
					type="button"
					onClick={() => onSortChange?.(nextSort(column.key, sort))}
					className="rounded-ds-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					{column.header}
				</button>
			) : (
				column.header
			)}
		</th>
	)
}

/** One data row — each cell from the column's renderer, or the row value at its key. */
function BodyRow<Row>({
	row,
	columns,
	cellPadding,
}: {
	row: Row
	columns: TableColumn<Row>[]
	cellPadding: string
}): ReactElement {
	return (
		<tr className="border-b border-ds-border last:border-b-0">
			{columns.map((column) => (
				<td key={column.key} className={cellPadding}>
					{column.cell ? column.cell(row) : String((row as Record<string, unknown>)[column.key] ?? '')}
				</td>
			))}
		</tr>
	)
}

export function Table<Row>({
	columns,
	rows,
	sort,
	onSortChange,
	emptyState,
	density = TableDensity.Comfortable,
	className,
	...props
}: TableProps<Row>): ReactElement {
	const cellPadding = DENSITY_CELL_PADDING[density]
	return (
		<table {...props} className={cn('w-full border-collapse text-ds-base text-ds-text', className)}>
			<thead>
				<tr className="border-b border-ds-border">
					{columns.map((column) => (
						<HeaderCell key={column.key} column={column} sort={sort} onSortChange={onSortChange} />
					))}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 ? (
					<tr>
						<td colSpan={columns.length} className="px-3 py-6 text-center text-ds-text-muted">
							{emptyState}
						</td>
					</tr>
				) : (
					rows.map((row, rowIndex) => (
						// The consumer owns row identity; index is the only stable key a
						// generic table can form without demanding an id on every row.
						// biome-ignore lint/suspicious/noArrayIndexKey: rows render in the given order and are never reordered here.
						<BodyRow key={rowIndex} row={row} columns={columns} cellPadding={cellPadding} />
					))
				)}
			</tbody>
		</table>
	)
}
